import { Component, computed, inject, OnDestroy, signal, SimpleChanges, ViewChild } from "@angular/core";
import { MalfunctionFilterType } from "../../helpers/types";
import { AuthService } from "../../services/AuthService";
import { ApiHelper } from "../../helpers/apihelper";
import { CommonModule, formatDate, Location } from "@angular/common";
import { MalfunctionFilterComponent } from "../../components/malfunction/malfunction-filter/malfunction-filter";
import { MessageService } from "../../services/messageservice";
import { Subscription as RxSubscription } from "rxjs";
import { ActivatedRoute } from "@angular/router";
import { Apollo } from "apollo-angular";
import { StorageHelper } from "../../helpers/storage/storagehelper";
import { CIM_QUERY } from "../../graphql/domain/cim";
import { CimResult } from "../../graphql/custom-types";
import { Impact, Notification, NotificationFilter, ProcessState } from "../../gql/generated";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { NavigationComponent } from "../../components/common/navigation/navigation";
import { WebarchiveComponent } from "../../components/malfunction/webarchive/webarchive";
import { LoaderComponent } from "../../components/common/loader/loader";
import { MalfunctionCardComponent } from "../../components/malfunction/malfunction-card/malfunction-card";
import { TimelineFooterComponent } from "../../components/malfunction/timeline-footer/timeline-footer";
import { FooterComponent } from "../../components/common/footer/footer";
import { reverseSortTickets } from "./malfunction.helpers";

@Component({
  selector: "page-malfunction",
  templateUrl: "malfunction.html",
  styleUrls: ["malfunction.scss"],
  imports: [
    CommonModule,
    FooterComponent,
    LoaderComponent,
    MalfunctionCardComponent,
    MalfunctionFilterComponent,
    MalfunctionFilterComponent,
    NavigationComponent,
    TimelineFooterComponent,
    TranslateModule,
    WebarchiveComponent,
  ],
})
export class MalfunctionPage implements OnDestroy {
  @ViewChild(MalfunctionFilterComponent) filter;
  public filterData = signal<MalfunctionFilterType | undefined>(undefined);
  public activeTypeFilters = signal<string[]>([]);
  public activeImpactFilters = signal<string[]>([]);
  public presetSubscriptionId = "none";
  public presetProductType = "none";
  public loading = true;
  public customerGuid: string;
  public hasMore = true;

  private loadMoreActive = false;
  private subEnableFilter: RxSubscription;
  private subDisableFilter: RxSubscription;
  private apolloQuery: any;
  private rxSubscriptions: RxSubscription[] = [];

  protected translate = inject(TranslateService);
  protected tickets = signal<Notification[]>([]);
  protected filterSubscriptionId = signal("");
  protected filterSubscriptionName = signal("");
  protected filterProductType = signal("");
  protected hasFocusedSubscription = computed(() => this.filterSubscriptionId() !== "");
  protected hasFocusedProductType = computed(() => this.filterProductType() !== "");
  protected focusProductTypeLightpath = computed(() => this.filterProductType() === "LightPath");
  protected focusProductTypeIP = computed(() => this.filterProductType() === "IP");
  protected focusProductTypeL2vpn = computed(() => this.filterProductType() === "L2VPN");
  protected focusProductTypeFirewall = computed(() => this.filterProductType() === "Firewall");
  protected focusProductTypePort = computed(() => this.filterProductType() === "Port");
  protected filterProductTypeKey = computed(() => {
    const key = this.focusProductTypeFirewall() ? "FW" : this.filterProductType();
    return `Subscription.${key}.Name`;
  });
  protected hasTicketsAvailable = computed(() => this.filteredTickets().length > 0);
  protected ticketsByActivityType = computed(() => {
    if (this.activeTypeFilters().length > 0) {
      return this.tickets().filter((ticket) => this.activeTypeFilters().includes(ticket.category));
    }
    return this.tickets();
  });
  protected filteredTickets = computed(() => {
    let tickets: Notification[] = this.ticketsByActivityType();
    if (this.activeImpactFilters().length > 0) {
      tickets = tickets.filter((ticket) => this.checkImpactFilter(ticket));
    }

    return reverseSortTickets(tickets);
  });

  constructor(
    public auth: AuthService,
    public api: ApiHelper,
    private msgsrv: MessageService,
    private route: ActivatedRoute,
    private location: Location,
    private apollo: Apollo,
  ) {
    this.presetSubscriptionId = this.route.snapshot.paramMap.get("subscriptionId");
    this.presetProductType = this.route.snapshot.paramMap.get("productType");
    this.customerGuid = localStorage.getItem("viewingCustomerGUID");

    const rxAuth = auth.userLoaded.subscribe((d) => {
      this.validateCustomerGuid();
      this.triggerFilter();
    });
    this.rxSubscriptions.push(rxAuth);

    const rxEnableFilter = this.msgsrv.enableMalfunctionFilter$.subscribe((filter: MalfunctionFilterType) => {
      this.filterData.set(filter);
      this.triggerFilter();
    });
    this.rxSubscriptions.push(rxEnableFilter);

    this.subEnableFilter = msgsrv.enableFilter$.subscribe((filter) => {
      switch (filter.type) {
        case "type":
          this.activeTypeFilters.update((current) => [...current, filter.value]);
          break;
        case "impact":
          this.activeImpactFilters.update((current) => [...current, filter.value]);
          break;
        default:
          break;
      }
    });

    this.subDisableFilter = msgsrv.disableFilter$.subscribe((filter) => {
      switch (filter.type) {
        case "type":
          this.activeTypeFilters.update((current) => [...current.filter((value) => value !== filter.value)]);
          break;
        case "impact":
          this.activeImpactFilters.update((current) => [...current.filter((value) => value !== filter.value)]);
          break;
        default:
          break;
      }
    });
  }

  ngOnDestroy(): void {
    if (this.apolloQuery) {
      this.apolloQuery.unsubscribe();
    }

    this.subEnableFilter.unsubscribe();
    this.subDisableFilter.unsubscribe();
    this.rxSubscriptions.forEach((s) => s.unsubscribe());
  }

  triggerFilter() {
    if (!this.auth.state.viewingCustomer.customerId) {
      return;
    }

    if (this.filterData()) {
      this.filterSubscriptionId.set(this.filterData().subscriptionId);
      this.filterSubscriptionName.set(this.filterData().subscriptionName);
      this.filterProductType.set(this.filterData().productType);
      this.loadMoreActive = this.filterData().loadMoreActive;
      this.hasMore = this.filterData().hasMore ?? true;
      this.updateUrl();

      this.fetchCimNotifications(
        this.filterProductType(),
        this.filterSubscriptionId(),
        this.filterData().filterStartDate,
        this.filterData().filterEndDate,
      );
    }
  }

  updateUrl() {
    let newUrl = "storing-en-onderhoud";
    if (this.hasFocusedProductType()) {
      newUrl += "/" + this.filterProductType;
    }

    if (this.hasFocusedSubscription()) {
      newUrl += "/" + this.filterSubscriptionId;
    }

    this.location.replaceState(newUrl);
  }

  async fetchCimNotifications(productType: string, subscriptionId: string, filterStartDate: any, filterEndDate: any) {
    this.loading = true;
    const customerId = StorageHelper.currentUser;
    const filter: NotificationFilter = {
      customerId,
      beginTimestamp: filterStartDate,
      endTimestamp: filterEndDate,
      impacts: [Impact.Down, Impact.ReducedRedundancy, Impact.ResilienceLoss, Impact.Unknown],
      statuses: [ProcessState.Open, ProcessState.Updated, ProcessState.Closed],
    };
    if (subscriptionId) {
      filter.subscriptionIds = [subscriptionId];
    } else {
      filter.productTypes = productType ? [productType] : [];
    }
    this.apollo
      .watchQuery<CimResult>({
        query: CIM_QUERY,
        variables: { filter },
      })
      .valueChanges.subscribe(({ data }) => {
        // TODO #206 Address technical debt Notifications/Messages/PlannedWorks
        const tickets = [...data.notifications].sort((a, b) => b.startTimestamp - a.startTimestamp);

        // if data is loaded from 'load more', append it to the existing set
        if (this.loadMoreActive) {
          if (tickets.length > 0) {
            this.tickets.update((current) => [...current, ...tickets]);
          } else {
            this.hasMore = false; // no more data to load; disable load more button
          }
        } else {
          // replace result
          this.tickets.set(tickets);
        }
        this.loading = false;
      });
  }

  /**
   * validate the customerGuid and force a switchViewingCustomer
   */
  validateCustomerGuid() {
    // if no presets, don't run the validation
    if (!this.presetSubscriptionId || !this.presetProductType) {
      return;
    }
  }

  checkActivityTypeFilter(item: Notification): boolean {
    return this.activeTypeFilters().includes(item.category);
  }

  checkImpactFilter(item: Notification): boolean {
    return this.activeImpactFilters().filter((value) => item.tags?.includes(value)).length > 0;
  }

  /**
   * compare the date of tis element to the date of the prev.
   * */
  isNewDate(index: number) {
    if (index === 0) {
      return true;
    }

    if (this.filteredTickets()[index - 1] !== undefined) {
      const prevElementDate = formatDate(this.filteredTickets()[index - 1].startTimestamp, "yyyy-MM-dd", "en-US");
      const thisElementDate = formatDate(this.filteredTickets()[index].startTimestamp, "yyyy-MM-dd", "en-US");

      return prevElementDate !== thisElementDate;
    }
    return true;
  }
}
